import { useEffect, useState } from "react";
import { useSession } from "../../lib/session.tsx";
import { ApiError, googleStartUrl } from "../../lib/api.ts";
import { loadRuntimeConfig } from "../../lib/runtime-config.ts";
import { accountErrorText } from "./auth-errors.ts";
import { GoogleMark } from "./GoogleMark.tsx";

/**
 * Вход.
 *
 * Основной путь это логин и пароль. Он не «настоящая авторизация вместо
 * заглушки», а такой же провайдер личности, как VK и Google: та же строка
 * `app_user`, та же таблица `identity` (раздел 5.10 CLAUDE.md). Поэтому когда
 * приедет вход через сервисы, у человека не появится второй аккаунт.
 *
 * Гостевой кнопки здесь по умолчанию НЕТ, и это не «пока не сделали». Гостевой
 * вход заводил настоящего пользователя любому, кто знает адрес ручки, а HTTP
 * Basic со стенда снят. Дверь закрыта флагом `ALLOW_GUEST_SIGNUP` на сервере;
 * сюда его значение приезжает в config.json. Кнопка при выключенном флаге
 * именно отсутствует, а не показывается неактивной: неактивная кнопка обещает,
 * что путь появится, а его нет.
 *
 * Ученику всё это ничего не ломает: он не приходит на этот экран вообще, он
 * входит по постоянной ссылке `/join/<код>`.
 */

/**
 * Маркер «мы ушли на Google». Ставится перед навигацией, снимается на
 * следующем маунте этого экрана.
 *
 * Он существует ради белого списка (раздел 3.1 CLAUDE.md): после полной
 * навигации таймаут поставить негде, наш JS уже выгружен. Если человек
 * вернулся сюда без сессии и маркер на месте, значит `accounts.google.com`
 * либо не открылся, либо открылся и не довёл до конца, и об этом надо сказать
 * прямо, а не оставлять его смотреть на ту же форму входа.
 *
 * `sessionStorage`, а не `localStorage`: маркер живёт ровно эту вкладку.
 */
const STARTED_KEY = "edu.google.startedAt";

/** Отдельно от разметки: коды ошибок это контракт с API, а не текст кнопки. */
function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    // Сервер отвечает одинаково на неизвестный логин и неверный пароль.
    // Интерфейс обязан говорить то же самое: подсказка «такого логина нет»
    // сводила бы на нет всю осторожность на той стороне
    if (err.code === "invalid_credentials") return "Неверный логин или пароль.";
    // Пароль верный, но занятия этого входа перенесены в другой аккаунт
    // (`docs/account-adopt-design.md`, 8.4): сказать, где они теперь
    if (err.code === "account_merged") return accountErrorText(err.code) ?? err.message;
    if (err.code === "too_many_attempts") {
      return "Слишком много попыток. Попробуйте через час.";
    }
    if (err.status === 429) return "Слишком много попыток. Попробуйте позже.";
    if (err.status === 404) return "Этот способ входа отключён.";
  }
  return err instanceof Error ? err.message : String(err);
}

export function SignIn() {
  const { signInWithPassword, signInAsGuest } = useSession();

  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [guestName, setGuestName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Пока конфиг не приехал, считаем дверь закрытой: мигнувшая и исчезнувшая
  // кнопка хуже, чем кнопка, появившаяся на сотню миллисекунд позже
  const [guestAllowed, setGuestAllowed] = useState(false);
  const [googleUrl, setGoogleUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    loadRuntimeConfig()
      .then(async (cfg) => {
        if (!alive) return;
        setGuestAllowed(cfg.allowGuestSignup);
        // Флаг из того же config.json, что и гостевой вход: на сервере он
        // выводится из наличия ключей Google, поэтому кнопка и маршрут
        // разъехаться не могут
        if (cfg.googleSignInEnabled) setGoogleUrl(await googleStartUrl());
      })
      .catch(() => {
        /* конфиг не прочитался: остаёмся при закрытой двери */
      });
    return () => {
      alive = false;
    };
  }, []);

  // Мы вернулись на экран входа, а сессии нет. Если маркер на месте, вход
  // через Google не состоялся молча: чаще всего это ограничения сети
  const [googleStuck, setGoogleStuck] = useState(false);
  useEffect(() => {
    try {
      if (sessionStorage.getItem(STARTED_KEY) === null) return;
      sessionStorage.removeItem(STARTED_KEY);
      // Наш callback уже сказал, что именно случилось, и баннер про это висит
      // выше. Второе сообщение «страница Google не открылась» рядом с ним
      // прямо противоречит первому: она открылась. Маркер снимаем в любом
      // случае, иначе он всплывёт на следующем заходе
      if (new URLSearchParams(window.location.search).has("auth_error")) return;
      setGoogleStuck(true);
    } catch {
      /* приватный режим Safari: живём без подсказки */
    }
  }, []);

  async function submitPassword() {
    if (!login.trim() || !password) return;
    setBusy(true);
    setErr(null);
    try {
      await signInWithPassword(login.trim(), password);
    } catch (e) {
      setErr(messageFor(e));
      // Пароль стираем, логин оставляем: перенабирать оба после опечатки
      // раздражает, а оставленный на экране пароль это тот же пароль на
      // чужом экране
      setPassword("");
      setBusy(false);
    }
  }

  async function submitGuest() {
    const displayName = guestName.trim();
    if (!displayName) return;
    setBusy(true);
    setErr(null);
    try {
      await signInAsGuest(displayName);
    } catch (e) {
      setErr(messageFor(e));
      setBusy(false);
    }
  }

  return (
    <main className="lobby">
      <h1>Вход</h1>

      {/*
        Настоящая форма, а не два поля рядом с кнопкой: браузер и менеджер
        паролей опознают вход только по form с полями autocomplete
        username/current-password. Без этого пароль не предложится и не
        сохранится, и человек начнёт держать его в заметках.
      */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submitPassword();
        }}
      >
        <label className="field">
          <span className="lbl">Логин</span>
          <input
            className="fld"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            name="username"
            autoComplete="username"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            maxLength={255}
          />
        </label>

        <label className="field">
          <span className="lbl">Пароль</span>
          <input
            className="fld"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            name="password"
            autoComplete="current-password"
            maxLength={1024}
          />
        </label>

        <div className="lobby__row">
          {/*
            Единственный акцент экрана. Логин с паролем это основной путь входа
            (см. шапку файла), а не запасной к сервисам: гостевой вход обычно
            выключен флагом, VK ещё заглушка, а Google зависит от чужого домена,
            который в режиме белого списка может не открыться вовсе
          */}
          <button
            type="submit"
            className="btn btn--primary"
            disabled={busy || !login.trim() || !password}
          >
            Войти
          </button>
        </div>
      </form>

      {err && <p className="err">{err}</p>}

      {guestAllowed && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submitGuest();
          }}
        >
          <label className="field">
            <span className="lbl">Или продолжить без аккаунта, как вас показывать</span>
            <input
              className="fld"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="Имя"
              autoComplete="nickname"
              maxLength={80}
            />
          </label>
          <div className="lobby__row">
            <button className="btn" type="submit" disabled={busy || !guestName.trim()}>
              Продолжить как гость
            </button>
          </div>
        </form>
      )}

      {/*
        Провайдеры входа.

        Google это ссылка, а не кнопка с обработчиком, и это принципиально:
        сервер отвечает редиректом на accounts.google.com, значит нужен переход
        верхнего уровня. Через fetch он упёрся бы в CORS чужого домена.

        VK остаётся неактивной заглушкой. Её фирменного оформления здесь нет
        намеренно: вид кнопки VK ID жёстко регламентирован (цвета, размеры,
        дословный текст «Войти с VK ID»), и несоответствие это официальная
        причина блокировки приложения. Рисовать её раньше, чем она заработает,
        значит показывать нарушение живым пользователям.
      */}
      <div className="lobby__providers" role="group" aria-label="Вход через сервисы">
        <button type="button" className="btn" disabled title="Появится в следующей вехе">
          Войти с VK ID
        </button>

        {googleUrl !== null && (
          <a
            className="provider"
            href={googleUrl}
            onClick={() => {
              try {
                sessionStorage.setItem(STARTED_KEY, String(Date.now()));
              } catch {
                /* приватный режим: уходим без маркера, вход от этого не ломается */
              }
            }}
          >
            <GoogleMark />
            Войти через Google
          </a>
        )}

        {googleStuck && (
          <p className="note">
            Похоже, страница Google не открылась. Так бывает при ограничениях
            сети. Ученику аккаунт не нужен: он входит по постоянной ссылке от
            преподавателя.
          </p>
        )}

        {/*
          Подпись постоянная, а не только при ошибке. Она про архитектуру, а не
          про сбой: путь «по ссылке от преподавателя» не зависит ни от одного
          внешнего сервиса и работает даже в режиме белого списка.
        */}
        <p className="muted">
          Если вход через сервис не срабатывает, войдите по ссылке от
          преподавателя: этот путь работает всегда.
        </p>
      </div>

      {/*
        Обзор продукта для того, кто попал сюда, не зная, что это за сервис.
        Ученику он не показывается лишним шумом: ученик на этот экран не
        приходит вовсе, он входит по /join/<код> (см. шапку файла).

        Обычный <a>, а не <Link>: /tour.html это статический файл рядом со
        сборкой, а не маршрут SPA. Роутер о нём не знает, и клиентский переход
        привёл бы к фолбэку на index.html, то есть обратно сюда же.
      */}
      <p className="muted lobby__tour">
        Первый раз здесь? <a href="/tour.html">Обзор сервиса за 4 минуты</a>
      </p>
    </main>
  );
}
